import {ApiClient} from '@hconnect/apiclient'
import {KpiCalculationError} from '@hconnect/common/types'
import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {MONTH_REVERSE_MAP} from '../consts'
import {
  ExpertSystemEquipmentType,
  HistoricalMtmQueryKey,
  MtmKpiValue,
  MtmKpiValueQueryKey,
  YearlyKpiValueQueryKey
} from '../types'

type AdjustMonthValuePayload = {
  hierarchyId: string
  value: number
  month: string
}

type AdjustMonthErrorPayload = {
  hierarchyId: string
  error: KpiCalculationError
  month: string
}

export const useAdjustMonthValue = (
  year: number,
  equipmentType: ExpertSystemEquipmentType,
  options?: UseMutationOptions<MtmKpiValue, AxiosError, AdjustMonthValuePayload>
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({hierarchyId, value, month}: AdjustMonthValuePayload) => {
      const yearMonth = `${year}-${MONTH_REVERSE_MAP[month]}`
      const path = `/expert-system/kpis/${equipmentType}/mtm/${hierarchyId}/${yearMonth}/value`

      const response = await ApiClient.axiosInstance.put<MtmKpiValue>(path, value, {
        headers: {
          // Need to override content type cause axios sets it to application/x-www-form-urlencoded if plain number is passed in body
          'Content-Type': 'application/json'
        }
      })
      return response.data
    },
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({queryKey: [MtmKpiValueQueryKey]})
      await queryClient.invalidateQueries({queryKey: [HistoricalMtmQueryKey]})
      await queryClient.invalidateQueries({queryKey: [YearlyKpiValueQueryKey]})
      await options?.onSuccess?.(data, variables, context, mutation)
    }
  })
}

export const useAdjustMonthError = (
  year: number,
  equipmentType: ExpertSystemEquipmentType,
  options?: UseMutationOptions<MtmKpiValue, AxiosError, AdjustMonthErrorPayload>
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({hierarchyId, error, month}: AdjustMonthErrorPayload) => {
      const yearMonth = `${year}-${MONTH_REVERSE_MAP[month]}`
      const path = `/expert-system/kpis/${equipmentType}/mtm/${hierarchyId}/${yearMonth}/error`

      const response = await ApiClient.axiosInstance.put<MtmKpiValue>(path, error, {
        headers: {
          // Need to override content type cause axios sets it to application/x-www-form-urlencoded if plain string is passed in body
          'Content-Type': 'application/json'
        }
      })
      return response.data
    },
    ...options,
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({queryKey: [MtmKpiValueQueryKey]})
      await queryClient.invalidateQueries({queryKey: [HistoricalMtmQueryKey]})
      await queryClient.invalidateQueries({queryKey: [YearlyKpiValueQueryKey]})
      await options?.onSuccess?.(data, variables, context, mutation)
    }
  })
}
